// ═══════════════════════════════════════════════════════════════════════════════
//  src/components/recorrido/voz.js
//  QUE EL RECORRIDO SE OIGA, Y QUE EL AVATAR SEPA CUÁNDO ESTÁ HABLANDO
//
//  ── LA VOZ ES UN FICHERO, Y ESO NO ES UN DETALLE ───────────────────────────
//  Rob: «quiero que se escuche como el coach real y que se pueda reproducir las
//  veces que sean necesarias, algo así realizamos con los saludos».
//
//  Los trece bloques del recorrido están grabados con la MISMA voz que Spacy
//  usa en clase —OpenAI `tts-1`, voz `echo`, la rama spanglish de
//  `backend-api/src/tts.py`— y viven en `audios/` como mp3. Se graban una vez
//  con `node graba_recorrido.mjs --grabar` y se reproducen infinitas veces.
//
//  Eso resuelve tres cosas a la vez:
//   · Suena a Spacy y no a otra persona. El alumno oye en la bienvenida la
//     misma voz que va a oír después; una voz distinta rompe al personaje
//     antes de empezar.
//   · No le cuesta Energía Spacy a nadie, lo abra las veces que lo abra: un
//     texto que siempre dice lo mismo no tiene por qué sintetizarse cada vez.
//   · Se paga una vez —unos seis centavos el guion entero— y no por alumno.
//
//  ── LO QUE HABÍA ANTES ERA UN ERROR, Y ASÍ LO DIJO ─────────────────────────
//  Mientras no existían los mp3 puse la voz del navegador (`speechSynthesis`)
//  como suplente. Rob: «actualmente tenemos una voz del sistema, eso es un
//  error». Tenía razón: suena a sistema operativo, y Spacy no puede sonar a
//  sistema operativo. Está retirada. Si un audio falta, el recorrido se queda
//  callado —se lee igual de bien— en vez de hablar con una voz que no es suya.
//
//  ── EL AVATAR ──────────────────────────────────────────────────────────────
//  El Coach ya sabe poner a Spacy a hablar: tiene su vídeo y su estado
//  `hablando`. Lo que faltaba era avisarle. Por eso todo lo de aquí llama a
//  `avisa(true/false)`: sin eso, el muñeco se quedaba quieto mientras sonaba
//  la voz, que es lo que Rob vio.
// ═══════════════════════════════════════════════════════════════════════════════

import { audioDe } from './audios';
// Mientras Spacy habla, los sonidos de interfaz se callan. Es la misma senal
// que usan la leccion y la Arena (`src/audio/fx.js`): un pitido encima de su
// voz suena a fallo, no a interfaz.
import { vozEnCurso } from '../../audio/fx';

/**
 * Dice un bloque del recorrido.
 *
 * @param {string} id      el del guion (`saludo`, `coach`, `cierre`…), que es
 *                         también el nombre del fichero: `recorrido-<id>.mp3`.
 * @param {(hablando:boolean) => void} avisa  se llama con `true` al empezar y
 *                         con `false` al acabar. El Coach lo engancha a su
 *                         estado `hablando`, que es lo que mueve al avatar.
 * @returns {() => void}   para callarlo (al pasar de paso, al cerrar).
 */
export function hayVoz(id) {
    return Boolean(audioDe(id));
}

export function habla(id, avisa) {
    const nada = () => { /* no había nada sonando */ };

    const fichero = audioDe(id);
    if (!fichero) {
        // Falta la grabación. Se calla y se sigue leyendo: es preferible al
        // suplente del sistema, que sonaba a otra persona.
        avisa?.(false);
        return nada;
    }

    try {
        const a = new Audio(fichero);
        const fin = () => { vozEnCurso(false); avisa?.(false); };
        a.onended = fin;
        a.onerror = fin;
        vozEnCurso(true);
        avisa?.(true);

        // ── SI EL NAVEGADOR LO BLOQUEA, SE ESPERA AL PRIMER TOQUE ─────────
        //  Rob, sobre la bienvenida: «esto no tiene audio».
        //
        //  Y no era que faltase el fichero. La bienvenida es el único bloque
        //  que suena SIN que el alumno haya tocado nada —está ahí desde que se
        //  abre la pantalla—, y los navegadores bloquean el sonido hasta que
        //  hay un gesto. `play()` devuelve una promesa rechazada, no un error
        //  visible: se queda mudo y no se entera nadie. En la batería no se
        //  veía porque Chromium sin ventana no aplica esa política, así que
        //  daba verde mientras en su pantalla no sonaba.
        //
        //  Si lo rechazan, se deja enganchado al primer toque de la pantalla,
        //  sea donde sea. Quien toque algo oye el saludo entero desde el
        //  principio, en vez de no oírlo nunca.
        let esperando = null;
        const suelta = () => {
            if (!esperando) return;
            window.removeEventListener('pointerdown', esperando);
            window.removeEventListener('keydown', esperando);
            esperando = null;
        };

        a.play().catch(() => {
            esperando = () => { suelta(); a.play().catch(fin); };
            window.addEventListener('pointerdown', esperando, { once: true, passive: true });
            window.addEventListener('keydown', esperando, { once: true });
        });

        return () => {
            suelta();
            try { a.pause(); } catch { /* ya estaba parado */ }
            fin();
        };
    } catch {
        avisa?.(false);
        return nada;
    }
}
